import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { DataRow } from "@outpave/ds";
import moment from "moment";
import { GET_TRANSACTIONS } from "graphql/transaction";
import CommonDateFormat from "enums/CommonDateFormat";
import { formatCashFull } from "utils/formatCash";
import { Inline, Stack } from "components/layout/reusableLayouts";
import CustomSkeleton from "components/common/CustomSkeleton";
import { RenderTransactionStatus } from "components/common/RenderTransactionStatus";
import TransactionDetailsDrawer from "components/common/transactions/TransactionDetailsDrawer";

/** Enough of a transaction to draw one row. */
interface RecentTransaction {
  id: string;
  merchant?: string | null;
  amount?: number | null;
  currency?: string | null;
  transactionDate?: string | null;
  createdAt?: string | null;
  status?: string | null;
  type?: string | null;
  disputeStatus?: string | null;
  workflowRequest?: { status?: string | null } | null;
}

/** One card's transactions, or one person's. */
type RecentTransactionListProps =
  | { cardId?: string; cardHolder?: never }
  | { cardHolder?: string; cardId?: never };

const RECENT_LIMIT = 10;

/**
 * The newest transactions as rows. A row opens the transaction's details over
 * whatever drawer holds the list, and closing it returns to that drawer.
 */
const RecentTransactionList: React.FC<RecentTransactionListProps> = ({
  cardId,
  cardHolder,
}) => {
  const { t } = useTranslation();
  const [openTransactionId, setOpenTransactionId] = useState<string | null>(
    null,
  );
  const { data, loading, refetch } = useQuery(GET_TRANSACTIONS, {
    variables: { cardId, cardHolder, first: RECENT_LIMIT },
    skip: !cardId && !cardHolder,
    fetchPolicy: "cache-and-network",
  });

  const transactions: RecentTransaction[] = (
    data?.transactions?.edges ?? []
  ).map((edge: { node: RecentTransaction }) => edge.node);

  if (loading && transactions.length === 0) {
    return <CustomSkeleton variant="list" />;
  }

  return (
    <Stack gap="var(--space-4)">
      <Typography variant="h5" component="h3">
        {t("Recent transactions")}
      </Typography>

      {transactions.length === 0 ? (
        <Typography
          variant="body1"
          sx={{ color: "var(--color-text-tertiary)" }}
        >
          {t("No transactions were found")}
        </Typography>
      ) : (
        <Stack gap="0">
          {transactions.map((transaction) => {
            const date = transaction.transactionDate ?? transaction.createdAt;
            return (
              <DataRow
                key={transaction.id}
                type="amount"
                title={transaction.merchant || t("Unknown merchant")}
                // The status sits by the date so the merchant keeps the whole first line.
                subtitle={
                  <Inline gap="var(--space-3)" wrap={false}>
                    {date
                      ? moment(date).format(CommonDateFormat.MMDDYYYY)
                      : "-"}
                    {transaction.status && (
                      <RenderTransactionStatus
                        transactionStatus={transaction.status}
                        disputeStatus={transaction.disputeStatus ?? undefined}
                        workflowStatus={
                          transaction.workflowRequest?.status ?? undefined
                        }
                        transactionType={transaction.type ?? undefined}
                        smallPill
                      />
                    )}
                  </Inline>
                }
                amount={formatCashFull(
                  transaction.amount ?? 0,
                  transaction.currency ?? "usd",
                )}
                onClick={() => setOpenTransactionId(transaction.id)}
              />
            );
          })}
        </Stack>
      )}

      {openTransactionId && (
        <TransactionDetailsDrawer
          open
          transactionId={openTransactionId}
          handleCloseDrawer={() => setOpenTransactionId(null)}
          handleRefreshTransactions={() => refetch()}
        />
      )}
    </Stack>
  );
};

export default RecentTransactionList;
